import { motion } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Hero() {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen bg-[#1A1614] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto min-h-screen grid lg:grid-cols-2">
        {/* Left: Text content */}
        <div className="flex flex-col justify-center px-6 md:px-12 lg:px-16 py-28 lg:py-20 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-7 max-w-xl"
          >
            {/* Accent line */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-[2px] bg-[#D4AF37]" />
              <p
                data-testid="hero-accent"
                className="font-accent text-[#D4AF37] text-xl tracking-wide"
              >
                From India to the World
              </p>
            </div>

            {/* Main heading */}
            <h1
              data-testid="hero-heading"
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#F9F8F6] tracking-tight leading-[1.08]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Built on Trust.
              <br />
              Delivered with{" "}
              <span className="text-[#D4AF37] italic">Precision</span>
            </h1>

            {/* Subtitle */}
            <p
              data-testid="hero-subtitle"
              className="text-[#F9F8F6]/60 text-base md:text-lg leading-relaxed max-w-md"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Premium leather and farm-fresh agro products. Sourced
              with integrity, exported with excellence.
            </p>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap items-center gap-6 pt-1"
              data-testid="hero-stats"
            >
              <div className="text-center">
                <p className="text-[#D4AF37] text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Reliability</p>
                <p className="text-[#F9F8F6]/50 text-xs font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}></p>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div className="text-center">
                <p className="text-[#D4AF37] text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Transparency</p>
                <p className="text-[#F9F8F6]/50 text-xs font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}></p>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div className="text-center">
                <p className="text-[#D4AF37] text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Consistency</p>
                <p className="text-[#F9F8F6]/50 text-xs font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}></p>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-col sm:flex-row items-start gap-4 pt-4"
            >
              <Button
                data-testid="hero-cta-quote"
                onClick={() => scrollTo("#inquiry")}
                className="rounded-full px-8 py-4 h-auto bg-[#8D5A34] text-white hover:bg-[#5D3A20] transition-all hover:scale-105 shadow-lg text-sm font-medium"
              >
                Request a Quote
                <ArrowRight size={16} className="ml-2" />
              </Button>
              <Button
                data-testid="hero-cta-services"
                onClick={() => scrollTo("#services")}
                variant="ghost"
                className="rounded-full px-8 py-4 h-auto text-[#F9F8F6]/70 hover:text-[#D4AF37] hover:bg-transparent transition-all text-sm font-medium underline underline-offset-4 decoration-[#D4AF37]/30"
              >
                Explore Services
              </Button>
            </motion.div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="absolute bottom-8 left-6 md:left-12 lg:left-16"
          >
            <button
              data-testid="hero-scroll-down"
              onClick={() => scrollTo("#about")}
              className="flex items-center gap-2 text-[#D4AF37]/80 hover:text-[#D4AF37] transition-colors"
            >
              <ArrowDown size={18} strokeWidth={2} className="animate-bounce" />
              <span className="text-sm font-medium tracking-wide" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Scroll to explore
              </span>
            </button>
          </motion.div>
        </div>

        {/* Right: Image collage */}
        <div className="relative hidden lg:flex items-center justify-center p-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full h-full max-h-[85vh] grid grid-rows-2 gap-4"
          >
            {/* Top image - Leather */}
            <div className="relative rounded-2xl overflow-hidden img-zoom">
              <img
                src="https://customer-assets.emergentagent.com/job_global-trade-hub-105/artifacts/ob72yysf_pexels-vlada-karpovich-4452395.jpg"
                alt="Premium leather hides"
                className="w-full h-full object-contain bg-[#2a1f18]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1614]/60 to-transparent" />
              <div className="absolute bottom-4 left-5">
                <span
                  className="text-white/90 text-sm font-semibold tracking-wide"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Leather
                </span>
                <span className="text-[#D4AF37] text-sm ml-2 font-accent">
                  Premium Hides & Crafted Goods
                </span>
              </div>
            </div>

            {/* Bottom image - Agro */}
            <div className="relative rounded-2xl overflow-hidden img-zoom">
              <img
                src="https://customer-assets.emergentagent.com/job_global-trade-hub-105/artifacts/0r8adcw5_pexels-cottonbro-9811521.jpg"
                alt="Fresh fruits and agro products"
                className="w-full h-full object-contain bg-[#1a2418]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1614]/60 to-transparent" />
              <div className="absolute bottom-4 left-5">
                <span
                  className="text-white/90 text-sm font-semibold tracking-wide"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Agro
                </span>
                <span className="text-[#4A6741] text-sm ml-2 font-accent">
                  Farm-Fresh Fruits & Vegetables
                </span>
              </div>
            </div>

            {/* Decorative gold border accent */}
            <div className="absolute -top-3 -right-3 w-24 h-24 border-t-2 border-r-2 border-[#D4AF37]/30 rounded-tr-2xl pointer-events-none" />
            <div className="absolute -bottom-3 -left-3 w-24 h-24 border-b-2 border-l-2 border-[#D4AF37]/30 rounded-bl-2xl pointer-events-none" />
          </motion.div>
        </div>

        {/* Mobile: background image hint */}
        <div className="lg:hidden absolute inset-0 opacity-10">
          <img
            src="https://customer-assets.emergentagent.com/job_global-trade-hub-105/artifacts/ob72yysf_pexels-vlada-karpovich-4452395.jpg"
            alt=""
            className="w-full h-full object-cover"
            aria-hidden="true"
          />
        </div>
      </div>
    </section>
  );
}
